import { Link, useSearchParams } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { usePropertyContext } from '@/context/PropertyContext';
import { cn } from '@/lib/utils';

/**
 * Header - Premium header for landing mode with property branding
 * 
 * Features:
 * - Immersive hero image with elegant gradient overlay
 * - Refined typography with serif font
 * - Accessible navigation with smooth animations
 * - Premium mobile menu
 * - Full WCAG 2.1 AA compliance
 */
export function Header() {
  const { config, propertyKey } = usePropertyContext();
  const [searchParams] = useSearchParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const property = config?.property;
  const heroImage = property?.images?.[0];

  // Build category links with property param preserved
  const buildCategoryLink = (categorySlug: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('category', categorySlug);
    if (propertyKey && !params.has('property')) {
      params.set('property', propertyKey);
    }
    return `/?${params.toString()}`;
  };

  // Build home link with property param preserved
  const buildHomeLink = () => {
    if (propertyKey) {
      return `/?property=${propertyKey}`;
    }
    return '/';
  };

  return (
    <header className="border-b border-border/50 bg-background" role="banner">
      {/* Hero Section with Property Image - Immersive */}
      {heroImage && (
        <div className="relative h-64 overflow-hidden sm:h-80 lg:h-96">
          {/* Background image with parallax-like effect */}
          <img
            src={heroImage.url}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover scale-105"
          />

          {/* Premium gradient overlay */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/25 to-black/5"
            aria-hidden="true"
          />

          {/* Subtle vignette */}
          <div
            className="absolute inset-0 image-overlay-vignette"
            aria-hidden="true"
          />

          {/* Property info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
            <div className="mx-auto max-w-7xl">
              {/* Property name - elegant serif */}
              <h1 className="font-serif text-4xl font-semibold text-white sm:text-5xl lg:text-6xl tracking-luxury">
                {property?.name}
              </h1>

              {/* Property description */}
              {property?.description && (
                <p className="mt-4 max-w-2xl text-base text-white/85 sm:text-lg leading-relaxed">
                  {property.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation Bar - Refined */}
      <nav
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
        aria-label="Main navigation"
      >
        <div className="flex h-20 items-center justify-between">
          {/* Logo/Home link */}
          <Link
            to={buildHomeLink()}
            className={cn(
              'flex items-center gap-2 rounded-lg',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              'transition-opacity hover:opacity-70 duration-300'
            )}
            aria-label={`${property?.name || 'Experiences'} - Home`}
          >
            {!heroImage && (
              <span className="font-serif text-2xl font-semibold text-foreground tracking-luxury">{property?.name || 'Experiences'}</span>
            )}
          </Link>

          <div className="flex items-center gap-4">
            {/* Mobile menu button - Premium styling */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={cn(
                'flex h-11 w-11 items-center justify-center rounded-xl sm:hidden',
                'text-muted-foreground',
                'border border-border/40 bg-card shadow-soft',
                'hover:bg-muted hover:text-foreground hover:border-border',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                'transition-all duration-300'
              )}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Menu className="h-5 w-5" aria-hidden="true" />
              )}
            </button>

            {/* Categories nav on desktop - Premium pills */}
            <div className="hidden items-center gap-2 sm:flex" role="list" aria-label="Experience categories">
              {config?.categories.slice(0, 4).map((category) => (
                <Link
                  key={category.id}
                  to={buildCategoryLink(category.slug)}
                  className={cn(
                    'inline-flex items-center gap-2 px-5 py-2.5 rounded-full',
                    'text-sm font-medium text-muted-foreground',
                    'hover:text-foreground hover:bg-muted/80',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    'transition-all duration-300'
                  )}
                  role="listitem"
                >
                  {category.icon && <span aria-hidden="true" className="text-base">{category.icon}</span>}
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile menu - Premium slide-down */}
        {isMobileMenuOpen && (
          <div 
            id="mobile-menu"
            className="border-t border-border/30 py-4 sm:hidden animate-slide-up"
            role="menu"
            aria-label="Mobile navigation menu"
          >
            <div className="space-y-1">
              {config?.categories.map((category) => (
                <Link
                  key={category.id}
                  to={buildCategoryLink(category.slug)}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl',
                    'text-sm font-medium text-muted-foreground',
                    'hover:text-foreground hover:bg-muted',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    'transition-all duration-200'
                  )}
                  role="menuitem"
                >
                  {category.icon && <span aria-hidden="true" className="text-lg">{category.icon}</span>}
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
